import OTPInput, { ResendOTP } from 'otp-input-react'
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { GTM_SIGN_OUT_REASON } from 'constants/gtm'
import { logoutStart } from 'store/redux-slices/login'
import { resendPhoneOTPStart, submitOTPStart, sendStrictPhoneOTPStart } from 'store/redux-slices/user'
import { closeModal } from 'store/redux-slices/modal'
import { MODALS } from 'constants/modals'
import './styles/index.scss'
import Button from 'components/Common/Button'
import { BUTTON_VARIANT } from 'utils/constants'

export const PhoneVerification = ({
  isStrict = false,
  signupData,
  onClose,
  returnTo,
  setDisableSubmit
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [OTP, setOTP] = useState('')
  const { userDetails } = useSelector(state => state.user)
  const { languageData, brandLogo } = useSelector(state => state.language)
  const { loading } = useSelector(state => state.user)

  const handleResendCodeClick = () => {
    if (isStrict) {
      dispatch(sendStrictPhoneOTPStart({
        phone: signupData?.phone,
        phoneCode: signupData?.phoneCode,
        signupData
      }))
    } else {
      dispatch(resendPhoneOTPStart())
    }
  }

  const handleVerifyClick = () => {
    if (isStrict) {
      dispatch(submitOTPStart({
        data: { otp: OTP },
        signupData,
        navigate,
        returnTo,
        setDisableSubmit
      }))
    } else {
      dispatch(submitOTPStart({
        data: { otp: OTP },
        onSuccess: () => {
          dispatch(closeModal(MODALS.PHONE_VERIFICATION_MODAL))
        }
      }))
    }
  }

  const handleCancelClick = () => {
    // For STRICT flow (pre-signup), cancel returns to form
    if (isStrict) {
      if (onClose) {
        onClose()
      } else {
        dispatch(closeModal(MODALS.PHONE_VERIFICATION_MODAL))
      }
    } else {
      // For MILD flow (post-login), cancel logs user out
      dispatch(logoutStart({ navigate, reason: GTM_SIGN_OUT_REASON.FROM_PHONE_VERIFICATION }))
      dispatch(closeModal(MODALS.PHONE_VERIFICATION_MODAL))
    }
  }

  // Update OTP input styles when values change
  useEffect(() => {
    const updateOTPStyles = () => {
      const inputs = document.querySelectorAll('.phone-verification-otp-input')
      inputs.forEach((input) => {
        if (input.value && input.value !== '') {
          input.classList.add('has-value')
        } else {
          input.classList.remove('has-value')
        }
      })
    }

    // Run immediately and after a short delay to catch library updates
    updateOTPStyles()
    const timer = setTimeout(updateOTPStyles, 100)

    return () => clearTimeout(timer)
  }, [OTP])

  return (
    <div className='phone-verification-content'>
      <div className='phone-verification-body'>
        <img src={brandLogo || `${process.env.REACT_APP_STATIC_IMAGE}/brand-logo.svg`} alt='Phone Verification' className='phone-verification-icon' />
        {/* Title */}
        <h2 className='phone-verification-title'>{languageData?.phoneVerification}</h2>

        {/* Description with phone number */}
        <p className='phone-verification-description'>
          {languageData?.phoneVerificationTextRandom || "Enter the code we've sent to your registered phone number"}{' '}
          <span className='phone-number-gradient'>
            {isStrict ? signupData?.phoneCode : userDetails?.phoneCode} {isStrict ? signupData?.phone : userDetails?.phone}
          </span>
        </p>

        {/* OTP Input */}
        <div className='otp-input-wrapper'>
          <OTPInput
            value={OTP}
            placeholder='------'
            onChange={setOTP}
            autoFocus
            OTPLength={6}
            otpType='number'
            disabled={false}
            className='otp-input-container'
            inputClassName='phone-verification-otp-input'
          />
        </div>

        {/* Buttons */}
        <div className='phone-verification-buttons'>
          <Button
            type='button'
            className='btn-verify'
            variant={BUTTON_VARIANT.SECONDARY}
            onClick={handleVerifyClick}
            loading={loading}
            disabled={OTP?.length !== 6}
          >
            {languageData?.verifyAndContinue || 'Verify & Continue'}
          </Button>

          <Button
            className='btn-logout'
            variant={BUTTON_VARIANT.TERTIARY}
            onClick={handleCancelClick}
          >
            {isStrict ? (languageData?.back || 'Back') : languageData?.headerLogout}
          </Button>
        </div>

        {/* Resend OTP Timer */}
        <div className='resend-otp-wrapper'>
          <ResendOTP
            renderButton={(buttonProps) => {
              return (
                <button {...buttonProps} className='resend-otp-text'>
                  {languageData?.resendOTPIn || 'Resend OTP in'} {buttonProps.remainingTime} {languageData?.seconds || 'sec'}
                </button>
              )
            }}
            renderTime={() => null}
            onResendClick={handleResendCodeClick}
            maxTime={60}
          />
        </div>

        {/* Help text block */}
        <div className='phone-verification-help-text'>
          <p className='help-text-message'>
            {languageData?.phoneVerificationHelpText || 'If you did not receive the code, please make sure you return to previous step and make sure the phone number provided by you is correct.'}
          </p>
          <p className='help-text-support'>
            {languageData?.phoneVerificationSupportText || 'You can always contact our'}{' '}
            <a
              href='/support'
              className='support-team-link'
            >
              {languageData?.supportTeam || 'Support Team'}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default PhoneVerification
